import { escapeHtmlText } from '../../core/format.js';

function stripAiChatTrailingDash(text: string): string {
  return String(text || '').replace(/\s+[-–—]\s*$/g, '').trim();
}

function cleanAiChatFormattingArtifacts(raw: string): string {
  let s = String(raw || '').replace(/\r\n/g, '\n');
  s = s.replace(/^\s*[*•\-–—]\s*$/gm, '');
  s = s.replace(/^(.+)\s+[-–—]\s*$/gm, function (_, line: string) {
    return stripAiChatTrailingDash(line);
  });
  s = s.replace(/:\s+[-–—](?=\s*(?:\n|$))/g, ':');
  s = s.replace(/\n{3,}/g, '\n\n');
  return s.trim();
}

export function sanitizeAiChatReplyText(raw: string): string {
  let s = String(raw || '').trim();
  if (!s) return '';
  try {
    if (typeof s.normalize === 'function') s = s.normalize('NFC');
  } catch (_) {}
  s = s.replace(/\uFFFD/g, '');
  s = s.replace(/[ \t]{2,}/g, ' ');
  s = cleanAiChatFormattingArtifacts(s);
  return s.trim();
}

function formatAiChatMarkdownInline(raw: string): string {
  let s = escapeHtmlText(raw);
  s = s.replace(/`([^`\n]+)`/g, '<code class="ai-chat-code">$1</code>');
  s = s.replace(/\*\*([^*\n]+)\*\*/g, '<strong>$1</strong>');
  s = s.replace(/__([^_\n]+)__/g, '<strong>$1</strong>');
  s = s.replace(/(^|[^*])\*([^*\n]+)\*([^*]|$)/g, '$1<em>$2</em>$3');
  return s;
}

function isAiChatListLine(line: string): string | null {
  const t = String(line || '').trim();
  if (!t) return null;
  if (parseAiChatSectionLine(t)) return null;
  const bullet = t.match(/^([*•\-–—])\s+(.+)$/);
  if (bullet && bullet[1] === '*' && /^\*\*/.test(bullet[2])) return bullet[2];
  if (bullet) return stripAiChatTrailingDash(bullet[2]);
  const num = t.match(/^\d+[.)]\s+(.+)$/);
  if (num) return stripAiChatTrailingDash(num[1]);
  return null;
}

function parseAiChatSectionLine(line: string): { title: string; body: string } | null {
  const t = String(line || '').trim();
  const m = t.match(/^(?:[*•\-–—]\s+)?\*\*([^*\n]+):\*\*\s*(.*)$/);
  if (!m) return null;
  return { title: m[1].trim(), body: stripAiChatTrailingDash(m[2]) };
}

function isAiChatSectionTitleOnly(line: string): boolean {
  return /^(?:[*•\-–—]\s+)?\*\*[^*\n]+:\*\*\s*$/.test(String(line || '').trim());
}

function normalizeAiChatMarkdownBlocks(raw: string): string {
  let s = cleanAiChatFormattingArtifacts(raw);
  s = s.replace(/^[\t ]*[-•*–—]\s+(\*\*[^*\n]+:\*\*)/gm, '$1');
  s = s.replace(/([.!?…])\s+(?=[*•\-–—]\s+\*\*)/g, '$1\n');
  s = s.replace(/([^\n])\s+(\*\*[^*\n]{2,}:\*\*)/g, '$1\n\n$2');
  s = s.replace(/^\s*[*•\-–—]\s*$/gm, '');
  return s;
}

function looksLikeAiChatSectionTail(text: string): boolean {
  const t = String(text || '').trim();
  if (!t) return false;
  if (/^(Этого|Если|Уточните|Тогда|Также|В целом|Итого|Для|С такой|Можно|Однако|Но|В любом|При этом|Важно|Обратите)/iu.test(t)) {
    return true;
  }
  return t.length >= 48;
}

function splitAiChatSectionTrailingProse(body: string): { sectionBody: string; tail: string } {
  const b = stripAiChatTrailingDash(body);
  if (!b) return { sectionBody: '', tail: '' };

  const sentenceEnd = b.match(/^(.+?[.!?…])\s+(.+)$/);
  if (sentenceEnd) {
    const first = sentenceEnd[1].trim();
    const rest = sentenceEnd[2].trim();
    if (rest && first.length <= 140 && looksLikeAiChatSectionTail(rest)) {
      return { sectionBody: first, tail: rest };
    }
  }

  const kw = b.match(/^(.{1,100}?)\s+(Этого|Если|Уточните|Тогда|Также|В целом|Итого|Для|С такой|Можно|Однако|Но|В любом|При этом)\b/iu);
  if (kw && kw.index != null) {
    const spec = kw[1].trim();
    const tail = b.slice(kw.index + kw[1].length).trim();
    if (spec && tail && spec.length <= 100) {
      return { sectionBody: spec, tail: tail };
    }
  }

  return { sectionBody: b, tail: '' };
}

function pushAiChatSectionHtml(out: string[], title: string, body: string): void {
  const split = splitAiChatSectionTrailingProse(body);
  out.push('<div class="ai-chat-section">');
  out.push('<div class="ai-chat-section-title">' + escapeHtmlText(title) + '</div>');
  if (split.sectionBody) {
    out.push('<p class="ai-chat-section-body">' + formatAiChatMarkdownInline(split.sectionBody) + '</p>');
  }
  out.push('</div>');
  if (split.tail) {
    out.push('<p class="ai-chat-p">' + formatAiChatMarkdownInline(split.tail) + '</p>');
  }
}

export function formatAiChatMarkdown(raw: string): string {
  const text = normalizeAiChatMarkdownBlocks(raw);
  const lines = text.split('\n');
  const out: string[] = [];
  let listType: 'ol' | 'ul' | null = null;
  let listItems: string[] = [];
  let paraLines: string[] = [];

  function flushList(): void {
    if (!listItems.length) return;
    const tag = listType === 'ol' ? 'ol' : 'ul';
    out.push('<' + tag + ' class="ai-chat-list">');
    for (let i = 0; i < listItems.length; i++) {
      out.push('<li>' + formatAiChatMarkdownInline(listItems[i]) + '</li>');
    }
    out.push('</' + tag + '>');
    listItems = [];
    listType = null;
  }

  function flushPara(): void {
    if (!paraLines.length) return;
    let joined = paraLines.join(' ').replace(/\s+/g, ' ').trim();
    joined = stripAiChatTrailingDash(joined);
    joined = joined.replace(/:\s+[-–—]\s*$/, ':');
    if (joined) out.push('<p class="ai-chat-p">' + formatAiChatMarkdownInline(joined) + '</p>');
    paraLines = [];
  }

  for (let li = 0; li < lines.length; li++) {
    const trimmed = lines[li].trim();
    if (!trimmed || /^[*•\-–—]$/.test(trimmed)) {
      flushList();
      flushPara();
      continue;
    }
    const section = parseAiChatSectionLine(trimmed);
    if (section) {
      flushList();
      flushPara();
      let sectionBody = section.body;
      if (!sectionBody && isAiChatSectionTitleOnly(trimmed)) {
        const bodyLines: string[] = [];
        while (li + 1 < lines.length) {
          const nextTrimmed = lines[li + 1].trim();
          if (!nextTrimmed || parseAiChatSectionLine(nextTrimmed) || isAiChatListLine(nextTrimmed) != null) break;
          bodyLines.push(nextTrimmed);
          li += 1;
        }
        sectionBody = stripAiChatTrailingDash(bodyLines.join(' ').replace(/\s+/g, ' ').trim());
      }
      pushAiChatSectionHtml(out, section.title, sectionBody);
      continue;
    }
    const item = isAiChatListLine(trimmed);
    if (item != null) {
      flushPara();
      const isOrdered = /^\d+[.)]\s+/.test(trimmed);
      const wantType = isOrdered ? 'ol' : 'ul';
      if (listType && listType !== wantType) flushList();
      listType = wantType;
      listItems.push(item);
      continue;
    }
    flushList();
    paraLines.push(trimmed);
  }
  flushList();
  flushPara();
  return out.length ? out.join('') : formatAiChatMarkdownInline(text);
}
